import React from "react";
import { ExternalLink } from "lucide-react";
import Tooltip from "../ui/Tooltip";
import { TENDON_WRAP_GEOM_GRID_CLASS } from "../../constants/ui";
import { stripNamespacePrefix } from "../../utils/nameUtils";
import PropertyPanelSection from "./PropertyPanelSection";
import { DEFAULT_WRAP_COLOR, colorToHex, materialSwatchStyle } from "./tendonPanelConstants";

const SpatialTendonWrapGeomsSection = ({
  open,
  onOpenChange,
  spatialWrapGeoms,
  primaryWrapMaterial,
  wrapColor,
  sites,
  onWrapColorChange,
  onWrapAlphaChange,
  onFocusGeom,
}) => (
  <PropertyPanelSection
    title={open ? `Wrap Geoms (${spatialWrapGeoms.length})` : "Wrap Geoms"}
    open={open}
    onOpenChange={onOpenChange}
    unmountOnClose
  >
    {open ? (
      <>
        <div className="flex items-center justify-between gap-3 rounded border border-gray-700/80 bg-gray-900/30 px-2 py-2 text-xs">
          <div className="min-w-0">
            <div className="text-gray-200">Wrap color</div>
            <div className="truncate text-[11px] panel-text-helper">
              {primaryWrapMaterial
                ? "Uses parsed wrap color as default"
                : "Creates a tendon-wrap material on edit"}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <input
              type="color"
              value={colorToHex(wrapColor)}
              onChange={(event) => onWrapColorChange(event.target.value)}
              className="h-7 w-8 rounded border border-gray-700 bg-gray-900 p-0.5"
              aria-label="Wrap geom color"
            />
            <input
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={Number(
                Array.isArray(wrapColor)
                  ? (wrapColor[3] ?? DEFAULT_WRAP_COLOR[3])
                  : DEFAULT_WRAP_COLOR[3]
              ).toFixed(2)}
              onChange={onWrapAlphaChange}
              className="w-16 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-100"
              aria-label="Wrap geom opacity"
            />
          </div>
        </div>
        {spatialWrapGeoms.length === 0 ? (
          <div className="panel-text-helper text-xs border border-gray-700 rounded p-2">
            No sphere or cylinder wrap geoms are referenced by this tendon.
          </div>
        ) : (
          spatialWrapGeoms.map(({ key, body, geom, routeIndex, sidesite, material }) => {
            const materialLabel =
              stripNamespacePrefix(material?.name) || geom.materials?.[0] || "No material";
            const bodyLabel = stripNamespacePrefix(body?.name) || body?.uuid || "body";
            const geomLabel = stripNamespacePrefix(geom?.name) || geom?.uuid || "wrap geom";
            const siteLabel = sites?.[sidesite]?.name || sidesite || "";
            return (
              <div
                key={key}
                className={`${TENDON_WRAP_GEOM_GRID_CLASS} rounded border border-gray-700/80 bg-gray-900/30 px-2 py-1.5 text-xs`}
              >
                <Tooltip content="Inspect wrap geom">
                  <button
                    type="button"
                    onClick={() => onFocusGeom(key)}
                    className="flex h-7 w-7 items-center justify-center rounded border border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-100"
                    aria-label={`Inspect wrap geom ${geomLabel}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </Tooltip>
                <div className="text-right panel-text-helper">{routeIndex + 1}</div>
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className="h-3 w-3 shrink-0 rounded-sm border border-gray-600"
                      style={materialSwatchStyle(material)}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 truncate text-gray-100">{geomLabel}</span>
                  </div>
                  <div className="mt-0.5 min-w-0 truncate text-[11px] panel-text-helper">
                    {bodyLabel} / {geom.kind || "geom"} / {materialLabel}
                    {siteLabel ? ` / side ${stripNamespacePrefix(siteLabel)}` : ""}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </>
    ) : null}
  </PropertyPanelSection>
);

export default SpatialTendonWrapGeomsSection;
